import {
  Button,
  CircularProgress,
  Paper,
  Toolbar,
  Typography,
  Select,
  MenuItem as MuiMenuItem,
  ListSubheader,
  FormHelperText,
} from '@material-ui/core';
import * as React from 'react';
import { ExternalMenuItemMapping, MenuItem, SetMenu } from 'interfaces';
import {
  DelayedLinearProgress,
  SnackbarContext,
  SortableTable,
} from 'components';
import { SortableTableHeader } from 'components/SortableTable/components';
import {
  ExternalOrderService,
  MenuItemService,
  SetMenuService,
} from 'services';
import SyncIcon from '@material-ui/icons/Sync';

const columns: SortableTableHeader[] = [
  { key: 'source', label: 'Source' },
  { key: 'external_item_id', label: 'External Item ID' },
  { key: 'external_item_name', label: 'External Name' },
  { key: 'mapped_to', label: 'Mapped To' },
];

const UNMAPPED = '__unmapped__';
const MI_PREFIX = 'mi:';
const SM_PREFIX = 'sm:';

// Encode the selection so a single Select can represent either a
// menu_item id or a set_menu id. Decoding happens in handleChange.
const encodeMappingValue = (m: ExternalMenuItemMapping): string => {
  if (m.menuItemId) return MI_PREFIX + m.menuItemId;
  if (m.setMenuId) return SM_PREFIX + m.setMenuId;
  return UNMAPPED;
};

interface Props {
  shopId: number;
}

export const MenuMappingsView: React.FC<Props> = ({ shopId }) => {
  const [loading, setLoading] = React.useState(true);
  const [mappings, setMappings] = React.useState<ExternalMenuItemMapping[]>([]);
  const [menuItems, setMenuItems] = React.useState<MenuItem[]>([]);
  const [setMenus, setSetMenus] = React.useState<SetMenu[]>([]);
  const [savingId, setSavingId] = React.useState<number | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [pulling, setPulling] = React.useState(false);
  const snackbar = React.useContext(SnackbarContext);

  const handlePullMenu = async () => {
    setPulling(true);
    try {
      const result = await ExternalOrderService.boltPullMenu(shopId);
      const r: any = result;
      const created = r?.created ?? 0;
      const updated = r?.updated ?? 0;
      const total = r?.total_skus ?? 0;
      snackbar.open(
        `Pulled ${total} SKUs from Bolt — ${created} new, ${updated} updated.`,
      );
      fetchAll();
    } catch (e) {
      snackbar.error(e);
    } finally {
      setPulling(false);
    }
  };

  React.useEffect(() => {
    setMappings([]);
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  const handleChange = async (
    mapping: ExternalMenuItemMapping,
    rawValue: string,
  ) => {
    let target:
      | { kind: 'menuItem'; id: number }
      | { kind: 'setMenu'; id: number }
      | { kind: 'unmapped' };

    if (rawValue === UNMAPPED) {
      target = { kind: 'unmapped' };
    } else if (rawValue.startsWith(MI_PREFIX)) {
      target = { kind: 'menuItem', id: Number(rawValue.slice(MI_PREFIX.length)) };
    } else if (rawValue.startsWith(SM_PREFIX)) {
      target = { kind: 'setMenu', id: Number(rawValue.slice(SM_PREFIX.length)) };
    } else {
      return;
    }

    // Capture the discriminated outcome up-front so the closure below
    // doesn't have to re-narrow the union (TS loses the discrimination
    // through the array map callback).
    const nextMenuItemId =
      target.kind === 'menuItem' ? target.id : null;
    const nextSetMenuId =
      target.kind === 'setMenu' ? target.id : null;

    setSavingId(mapping.id);
    setError(null);
    try {
      await ExternalOrderService.updateMapping(mapping.id, target);
      setMappings((prev) =>
        prev.map((m) => {
          if (m.id !== mapping.id) return m;
          const updated = { ...m };
          updated.menuItemId = nextMenuItemId;
          updated.setMenuId = nextSetMenuId;
          updated.menuItem = nextMenuItemId
            ? menuItems.find((mi) => mi.id === nextMenuItemId)
            : undefined;
          updated.setMenu = nextSetMenuId
            ? setMenus.find((sm) => sm.id === nextSetMenuId)
            : undefined;
          return updated;
        }),
      );
    } catch (e) {
      setError((e as Error)?.message || 'Failed to update mapping');
    } finally {
      setSavingId(null);
    }
  };

  const rows = mappings.map((mapping) => ({
    key: `mapping-${mapping.id}`,
    cells: [
      {
        key: 'source',
        display: mapping.externalOrderSource?.name ?? '—',
      },
      { key: 'external_item_id', display: mapping.externalItemId },
      {
        key: 'external_item_name',
        display: mapping.externalItemName ?? '—',
      },
      {
        key: 'mapped_to',
        display: (
          <Select
            value={encodeMappingValue(mapping)}
            onChange={(e) => handleChange(mapping, e.target.value as string)}
            disabled={
              savingId === mapping.id ||
              (menuItems.length === 0 && setMenus.length === 0)
            }
            style={{ minWidth: 280 }}
          >
            <MuiMenuItem value={UNMAPPED}>
              <em>— Unmapped —</em>
            </MuiMenuItem>
            <ListSubheader disableSticky>Menu items</ListSubheader>
            {menuItems.map((mi) => (
              <MuiMenuItem key={`mi-${mi.id}`} value={MI_PREFIX + mi.id}>
                {mi.title}
              </MuiMenuItem>
            ))}
            <ListSubheader disableSticky>Set menus</ListSubheader>
            {setMenus.map((sm) => (
              <MuiMenuItem key={`sm-${sm.id}`} value={SM_PREFIX + sm.id}>
                {sm.name}
              </MuiMenuItem>
            ))}
          </Select>
        ),
      },
    ],
  }));

  return (
    <React.Fragment>
      <DelayedLinearProgress loading={loading} />
      <Paper style={{ overflow: 'auto' }}>
        <Toolbar style={{ justifyContent: 'space-between' }}>
          <Typography variant="h6">Menu item mappings</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePullMenu}
            disabled={pulling}
            startIcon={
              pulling ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SyncIcon fontSize="small" />
              )
            }
          >
            {pulling ? 'Pulling…' : 'Pull Menu from Bolt'}
          </Button>
        </Toolbar>
        {error && (
          <FormHelperText error style={{ paddingLeft: 16 }}>
            {error}
          </FormHelperText>
        )}
        <SortableTable
          columns={columns}
          rows={rows}
          emptyTableText="No external menu items seen yet. Mappings are auto-created when a Bolt/Wolt order arrives with an unknown item id."
          disablePagination
        />
      </Paper>
    </React.Fragment>
  );

  async function fetchAll() {
    try {
      const [m, items, sets] = await Promise.all([
        ExternalOrderService.getMappings({ shopId }),
        MenuItemService.getAll(),
        SetMenuService.get(),
      ]);
      setMappings(m);
      // Only offer THIS shop's menu items. Shop membership rides on
      // menuCategory.menuSection.shopId (items have no shop_id of their
      // own). Items with a missing chain (deleted category) stay visible
      // rather than vanishing silently. Added 2026-07-14 after 14 Bolt
      // To Go SKUs were accidentally mapped to Street Food items.
      setMenuItems(
        items.filter((mi) => {
          const itemShopId = (mi as any).menuCategory?.menuSection?.shopId;
          return itemShopId === undefined || itemShopId === shopId;
        }),
      );
      // Filter set_menus to those that belong to the selected shop (so
      // a To Go set_menu doesn't show up in Street Food mapping dropdown).
      setSetMenus((sets as SetMenu[]).filter((sm) => sm.shopId === shopId));
    } catch {
      setMappings([]);
      setMenuItems([]);
      setSetMenus([]);
    } finally {
      setLoading(false);
    }
  }
};
